import applyImageOptimizations from '@src/utility/applyImageOptimizations';
import React, { useState } from 'react';
import styled from 'styled-components';

import DeleteIcon from '../../svgs/DeleteIcon';
import DownloadIcon from '../../svgs/DownloadIcon';
import EllipsisIcon from '../../svgs/EllipsisIcon';
import { assetDisplayTitle } from '../../utility/files';
import { handleDownloadMedia } from '../../utility/general';
import { useClickAway } from '../../utility/useClickAway';

export type Asset = {
  id: string;
  title: string;
  url?: string;
  downloadUrl?: string;
};

type Props = {
  asset: Asset;
  onDelete?: (asset: Asset) => void | Promise<void>;
  renderImage?: () => React.ReactNode;
};

const AssetItem = (props: Props) => {
  const { asset, onDelete, renderImage } = props;
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const title = assetDisplayTitle(asset);
  return (
    <Container key={asset.id} isMenuOpen={isMenuOpen}>
      {renderImage ? (
        renderImage()
      ) : asset.url ? (
        <img src={applyImageOptimizations(asset.url)} />
      ) : (
        <div className="skeleton" />
      )}

      <span>{title}</span>

      <div className="asset-menu" onClick={() => setIsMenuOpen(true)}>
        <div className="asset-menu-btn">
          <EllipsisIcon fillColor="var(--accent)" />
        </div>
        {isMenuOpen && asset.url && (
          <AssetDropdown
            title={title}
            file={{
              url: asset.downloadUrl || asset.url,
              fileName: title,
              id: asset.id,
            }}
            onClose={() => setIsMenuOpen(false)}
            onDelete={onDelete && (() => onDelete(asset))}
            style={{ position: 'absolute', top: '28px' }}
          />
        )}
      </div>
    </Container>
  );
};

const Container = styled.div<{ isMenuOpen: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: relative;

  > img {
    width: 100%;
    border-radius: 8px;
    overflow: hidden;
    aspect-ratio: 1;
    object-fit: contain;
  }

  > .skeleton {
    width: 100%;
    aspect-ratio: 1;
    border-radius: 8px;
    background: var(--border);
  }

  > span {
    color: var(--text-muted);
    font-size: var(--text-sm);
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }

  > .asset-menu {
    position: absolute;
    top: 8px;
    right: 8px;

    > .asset-menu-btn {
      display: flex;
      background: var(--border);
      border-radius: 4px;
      padding: 8px;
      cursor: pointer;
      opacity: ${({ isMenuOpen }) => (isMenuOpen ? 1 : 0)};
      transition: var(--transition-normal);
    }
  }

  &:hover > .asset-menu > .asset-menu-btn {
    opacity: 1;
  }
`;

type AssetDropdownProps = {
  title: string;
  file: { url: string; fileName: string; id: string };
  onClose: () => void;
  onDelete?: () => void | Promise<void>;
  style?: React.CSSProperties;
};

const AssetDropdown = (props: AssetDropdownProps) => {
  const { title, file, onClose, onDelete, style } = props;
  const ref = useClickAway(onClose);
  return (
    <DropdownMenu ref={ref} style={style}>
      <h3>{title}</h3>
      <div
        className="action-btn action-download"
        onClick={e => handleDownloadMedia(e, file)}
      >
        <span>Download</span>
        <DownloadIcon
          width="24"
          height="24"
          strokeColor="var(--text-secondary)"
        />
      </div>
      {onDelete && (
        <div
          className="action-btn action-delete"
          onClick={async () => {
            await onDelete();
            onClose();
          }}
        >
          <span>Delete</span>
          <DeleteIcon
            width="24"
            height="24"
            strokeColor="var(--text-secondary)"
          />
        </div>
      )}
    </DropdownMenu>
  );
};

const DropdownMenu = styled.div`
  position: absolute;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: var(--background);
  box-shadow: var(--elevation-toast);
  padding: 8px 16px;
  width: 280px;
  display: flex;
  flex-direction: column;
  z-index: 1;

  > * {
    padding: 16px 0;
  }

  > *:not(:last-child) {
    border-bottom: 1px solid var(--border);
  }

  > h3 {
    color: var(--text-secondary);
    font-size: 16px;
    font-weight: var(--font-weight-bold);
    margin: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    &:hover {
      overflow: auto;
      word-break: break-word;
      text-overflow: initial;
      white-space: initial;
    }
  }

  > .action-btn {
    display: flex;
    align-items: center;
    justify-content: space-between;
    transition: var(--transition-normal);
    &:hover {
      padding-left: 4px;
      padding-right: 4px;
      cursor: pointer;
    }
    &.action-download:hover {
      color: var(--primary);
      svg path {
        stroke: var(--primary);
      }
    }
    &.action-delete:hover {
      color: var(--destructive);
      svg path {
        stroke: var(--destructive);
      }
    }
  }
`;

export default AssetItem;
